/**
 * main.js — 前台（公開站）JS entry
 *
 * 與後台 admin.js 分離：本檔只載前台外殼（top-bar / site-nav / hero / AI widget）、
 * 共用元件（render-cards / modal / compare-bar / scroll-reveal / carousel）與各前台
 * 頁面控制器。後台 / auth / member 頁走 admin.js，不經此檔。
 *
 * 怎麼被載入：
 *   vite-plugin-html 對「每一頁」只注入單一 entry <script src="/src/main.js">。
 *   這代表頁面 HTML 自己手寫的第二支 <script> 會被外掛剝掉（vite 剝 inline script 陷阱），
 *   故所有頁面控制器（product-compare / article-detail / product-detail / clinic-detail /
 *   articles / clinics / products / search / error-404）改由本檔的中央 import 鏈載入，
 *   不依賴 page HTML 的 <script> 標籤。
 *
 * 設計原則：
 *   - 各頁面控制器自帶「root 守門早退」——import 時即 querySelector 自己的容器，找不到
 *     就 return，故非該頁不執行、不報錯。一條 import 鏈安全涵蓋全站所有頁。
 *   - import 順序有意義：render-cards 必先於 scroll-reveal / carousel（後兩者 import 時
 *     即查 DOM，卡片需先 render 出來才掃得到）。
 *
 * 已知陷阱：
 *   - vite 剝 inline script：見上，頁面 JS 必須掛這條鏈，HTML 自帶 <script> 無效。
 *   - SynologyDrive 背景同步觸發 Vite HMR full reload（見下方 scroll 還原邏輯）。
 *   - CSS 不在此 import：由 index.html / _head.html 的 <link> render-blocking 載入，
 *     避免 dev 雙載 + build 重複輸出兩份 CSS chunk。
 */

// 分類 section 平滑捲動的 reduced-motion 判定：與 products.js 分類 chip 點擊共用同一 util，
// 供下方「帶 hash 導航捲到 section」沿用相同機制（見 load handler），不另發明動畫。
import { prefersReducedMotion } from './scripts/utils/dom.js'

// 禁用瀏覽器 reload 自動還原 scroll position，避免 hero 圖片 layout shift 造成頁面切掉
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

// iOS Safari 預設不套用 :active 觸控回饋，除非 document / body 或元素自身掛有 touchstart handler
//（Apple 官方行為，不會修）。全域掛一個 no-op passive listener 即全站解鎖 :active 按下態；
// passive 明示不 preventDefault 故不影響捲動、空 handler 不讀 event，加一次涵蓋全站。
document.addEventListener('touchstart', () => {}, { passive: true });
// back_forward 與 reload 都還原離開前存下的 scrollY；純 navigate（全新導航）才歸頂。
// SynologyDrive 背景同步會觸發 Vite HMR full reload，舊版無條件 scrollTo(0,0) 會在使用者
// 捲到一半時把畫面拉回頂端（scroll bug 根因）。改判 navigation type 後，reload 時保留捲動位置。
//
// 純 navigate（讀不到有效存值）才預設強制歸頂（今日修的 hero 圖 layout-shift bug 依賴此行為）——但若 URL 帶
// 「命中頁內 section」的 hash（如麵包屑 /products#laser），改捲到該 section，不再把 hash 踩掉歸頂。
// 捲動沿用 products.js 分類 chip 的同一機制（scrollIntoView block:'start' + reduced-motion 直跳），
// 在 load（圖片/字型 layout 已定）時執行，與既有防位移時序一致，不新增 race condition。
// 捲動位置 key：write（pagehide 存）與 read（load 的 back_forward / reload 還原）共用同一函式，確保兩端
// 字串完全一致——分岔即靜默失效。含 pathname + search，避免不同頁 / 不同 query 變體互撞。
const scrollStoreKey = () => 'scrollY:' + window.location.pathname + window.location.search;

// carousel 橫向捲動位置 key：與 scrollY 同樣含 pathname + search（避免不同頁 / query 變體互撞），
// 再加一段識別「是哪個 carousel」——優先用元素 id；本站首頁 .clinic-list / .article-list 及
// products / article-detail 的輪播列皆無 id，故 fallback 用該元素在本頁 [data-carousel] 清單裡的 index。
// fallback 假設「同一頁面 carousel 的數量與 DOM 順序在 write（pagehide）與 read（load）兩端一致」——
// 本站 carousel 由 build 期靜態 markup 決定、載入後不新增/刪除/重排，故成立；write/read 共用本函式，字串分岔即靜默失效。
const carouselScrollKey = (el, index) =>
  'carouselScrollLeft:' + window.location.pathname + window.location.search + ':' + (el.id || index);

/**
 * 還原捲動位置（垂直 scrollY + carousel 橫向 scrollLeft）與 hash 錨點捲動，供 load 與
 * pageshow(persisted=true) 兩個入口共用呼叫（避免程式碼重複）。
 *
 * 為何需要 pageshow 第二入口：iOS bfcache 還原只觸發 pageshow，完全不觸發 load —— 若只掛
 * load，凡是 bfcache 成功保留頁面的情境，本檔沒有任何機會執行修正，只能仰賴瀏覽器原生還原
 * （無法補正還原後 late-load 圖片 / 字型造成的版面位移）。
 *
 * @param {boolean} bfcacheRestore 是否由 pageshow 的 e.persisted===true 呼叫。此旗標是瀏覽器對
 *   「這確實是 bfcache 還原」的直接確認，比 navType 推斷更可靠——bfcache 還原時這裡讀到的
 *   navType 反映的是本頁「第一次載入」當下的型別（很可能是 'navigate'，不會因為現在改走
 *   pageshow 入口而改變），故為 true 時直接視為返回情境、不受 navType 判定左右；由 load 呼叫
 *   時固定傳 false，判定邏輯與修復前完全一致（不改變既有行為）。
 */
function restoreScrollPosition(bfcacheRestore) {
  const navType = performance.getEntriesByType('navigation')[0]?.type;
  const isReturnLike = bfcacheRestore || navType === 'back_forward' || navType === 'reload';

  // carousel 橫向位置還原 —— 必須「先於」下方 scrollY 還原（同步、同一函式、盡量早），原因：
  // carousel.js 的 setupCardReveal 兩段式 observer 中，第 1 段 sectionObserver 只在 .clinics 區塊「垂直」
  // 捲入視窗才 arm 第 2 段 cardObserver。reload 初始 scrollY=0（區塊在折線下方）→ sectionObserver 尚未觸發；
  // 待下方 scrollY 還原把頁面跳到區塊、區塊入視窗才 arm cardObserver。故只要在 scrollY 還原「之前」把每個
  // carousel 的 scrollLeft 復位，cardObserver 開始觀察時看到的水平可見卡片集就與 reload 前一致 → 逐張 reveal
  // 覆蓋正確，不會只剩卡 1 露出、其餘卡永遠卡在 opacity:0。不引入 setTimeout / 非同步等待（避免過度設計）。
  if (isReturnLike) {
    document.querySelectorAll('[data-carousel]').forEach((el, index) => {
      try {
        const savedLeft = Number(sessionStorage.getItem(carouselScrollKey(el, index)));
        if (Number.isFinite(savedLeft)) el.scrollLeft = savedLeft;
      } catch (_) { /* 私密瀏覽 / 配額限制讀取失敗忽略，該 carousel 退化為起點 0 */ }
    });
  }

  const targetId = window.location.hash.slice(1);
  const target = targetId ? document.getElementById(targetId) : null;
  if (target) {
    target.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  } else {
    // back_forward（原生上一頁/下一頁，bfcache 未凍結）、reload，或 bfcache 還原 → 優先還原離開前
    // 存下的 scrollY。讀不到有效值（key 不存在 / 損毀 / 純 navigate 導航）就落回歸頂——graceful
    // default，非 regression。
    const savedY = isReturnLike
      ? (() => { try { return Number(sessionStorage.getItem(scrollStoreKey())); } catch (_) { return NaN; } })()
      : NaN;
    // 還原捲動必須瞬時：全域 reset.css html{scroll-behavior:smooth} 下，2-arg scrollTo 與 behavior:'auto'
    // 都會繼承 smooth → 從頂端「動畫捲」到還原點（可見的錯誤捲動）。顯式 behavior:'instant' 強制瞬跳。
    window.scrollTo({ top: Number.isFinite(savedY) ? savedY : 0, behavior: 'instant' });
  }
}

window.addEventListener('load', () => restoreScrollPosition(false));

// bfcache 還原（iOS 常見，尤其記憶體壓力下）不觸發 load，只觸發 pageshow + persisted=true——若只
// 掛 load，凡是 bfcache 成功保留頁面的情境本檔完全沒有機會執行。fresh load 的 pageshow 也會觸發
// 但 persisted 為 false，此時完全略過，避免跟 load 監聽重複執行同一段還原（兩者互斥：bfcache
// 還原時 load 不會 fire，fresh load 時 persisted 恆為 false，不會同時執行兩次）。
window.addEventListener('pageshow', (e) => {
  if (!e.persisted) return;
  restoreScrollPosition(true);
});

// 存捲動位置給「原生上一頁/下一頁」還原：bfcache 凍結頁面時瀏覽器自會復位，但記憶體壓力下
// （尤其 iOS Safari）bfcache 常被丟棄 → 回上一頁是全新 load，上面 else 會歸頂、丟失列表捲動位置。
// 用 pagehide（非 beforeunload/unload —— 後兩者會讓瀏覽器放棄 bfcache）：本站既有慣例
// （carousel.js / products.js 等皆用）。sessionStorage 在無痕/配額用盡情境可能 throw，故包 try/catch。
window.addEventListener('pagehide', () => {
  try {
    sessionStorage.setItem(scrollStoreKey(), String(window.scrollY));
  } catch (_) { /* 私密瀏覽 / 配額限制寫入失敗忽略，還原自動退化為歸頂 */ }

  // 同步存每個 carousel 自己的橫向 scrollLeft（reload 時瀏覽器會把 scrollLeft 重置為 0，不還原則
  // setupCardReveal 的 cardObserver 會以「只有卡 1 露出」重新初始化，其餘卡永遠停在 opacity:0）。
  // 通用 [data-carousel] selector 一次涵蓋首頁 .clinic-list / .article-list 及 products / article-detail
  // 各輪播列；非該頁時 querySelectorAll 回空集合自動 no-op。每個獨立包 try/catch，單一寫入失敗不影響其他。
  document.querySelectorAll('[data-carousel]').forEach((el, index) => {
    try {
      sessionStorage.setItem(carouselScrollKey(el, index), String(el.scrollLeft));
    } catch (_) { /* 私密瀏覽 / 配額限制寫入失敗忽略，該 carousel 還原自動退化為起點 0 */ }
  });
});

// CSS 入口改由 index.html / _head.html 的 <link rel="stylesheet" href="/src/styles/main.css"> 載入
// （render-blocking，dev 與 build 首次 paint 即有完整樣式，消除 dev FOUC）。
// 此處不再 import CSS，避免 dev 雙載 + build 重複輸出兩份 CSS chunk。
import './scripts/top-bar.js'
import './scripts/site-nav.js'
import './scripts/chat-dialog.js'
import './scripts/back-to-top.js'
// split-text：拆三 section 標題逐字 + IO 捲動進場（v2 起 hero__title 改回整塊淡入，不再經此檔）
import './scripts/split-text.js'
import './scripts/hero-slider.js'
// hero 沉浸底色層：量首屏高度寫 --hero-immersion-height + IO toggle nav.is-immersive 兩態
import './scripts/hero-immersion.js'
// hero icon 3D（three.js WebGL 自轉）— 漸進增強 + 效能緩解：
//   - 引擎與 three 皆走「延後動態 import」，不進初始 bundle、不擋首屏 critical path。
//   - 首屏先顯示靜態預渲染 PNG（<img>，eager/fetchpriority），idle 後 3D 接管、換掉海報。
//   - requestIdleCallback（不支援則 setTimeout 退化）確保 LCP / 首屏互動先完成才下載 732KB chunk。
//   - reduced-motion 由引擎內部 gate：直接不抓 chunk、不啟動，保留靜態 PNG。
{
  const startHeroIcon3D = () => {
    import('./scripts/hero-icon-3d.js')
      .then((m) => m.initHeroIcon3D())
      .catch(() => {}) // 載入/初始化失敗 → 靜態 PNG 海報留著當 fallback
  }
  if ('requestIdleCallback' in window) {
    requestIdleCallback(startHeroIcon3D, { timeout: 3000 })
  } else {
    setTimeout(startHeroIcon3D, 1500)
  }
}
// render-cards 必先於 scroll-reveal / carousel — 後兩者在 import 時即查 DOM
import './scripts/components/render-cards.js'
// S3 modal primitive — auto-init via [data-modal-trigger]
import './scripts/components/modal.js'
// S3 batch 2 — auto-init via [data-compare-bar]
import './scripts/components/compare-bar.js'
// 頁面控制器：各自有 root 守門早退（找不到自己的容器就 return），非該頁不執行、不報錯。
// vite-plugin-html 每頁只注入單一 entry（/src/main.js）會剝掉手寫的第二支 script，
// 故這兩支改由中央 import 鏈載入，不靠 page HTML 的 <script> 標籤。
import './scripts/pages/product-compare.js'
import './scripts/pages/article-detail.js'
// product-detail 縮圖切換主圖（root 守門早退 .product-gallery__main-img，非該頁不執行）。
// 同 product-compare / article-detail：頁面自帶 <script> 會被 vite-plugin-html 剝掉，故走中央鏈。
import './scripts/pages/product-detail.js'
// product-detail 規格表渲染（root 守門早退 [data-product-specs]）：對齊比較頁 10 項 + 劑型/字號，
// 與 product-compare 共用 compare-cell.js（bool/badge/rating 視覺一致）。
import './scripts/pages/product-detail-specs.js'
// clinic-detail 諮詢表單控制器（root 守門早退：找不到 #consult-form 即 return，非該頁不執行）
import './scripts/pages/clinic-detail.js'
// contact 留言表單 DEMO 假成功（root 守門早退 .contact-form，非該頁不執行）：
// preventDefault 中和 action="/api/contact" 的 404 + inline .alert--success 成功回饋（保留前端驗證）。
import './scripts/pages/contact-demo.js'
// articles 列表頁：分類 chip + 關鍵字搜尋篩選 + client 分頁（root 守門早退，非該頁不執行）
import './scripts/pages/articles.js'
// clinics 列表頁：client 分頁（root 守門早退 .clinic-grid，非該頁不執行）
import './scripts/pages/clinics.js'
// products 列表頁：分類 chip + 關鍵字搜尋篩選（共用 list-filter.js，root 守門早退）
import './scripts/pages/products.js'
// search 結果頁：回顯 ?q= + 類型 tab 委派（root 守門早退，非該頁不執行）
import './scripts/pages/search.js'
// 404 頁：巨數字 fuzzy-text canvas 特效（root 守門早退 [data-fuzzy-text]，PRM 顯回純文字）
import './scripts/pages/error-404.js'
// S3 batch 3 — B 端 complex：cr-form-card.js（~39KB）只有帶 [data-cr-form] 的頁面用到
// （前台僅 auth/clinic-register 一頁），改頁面守門的 dynamic import——有 form 才下載該 chunk，
// 其餘頁不再背 39KB。模組底部自帶 auto-init（import 即 bindAll），故 import() 完成即接線。
// admin-sidebar.js 已退役（深藍頂部 nav 重構），admin 互動由 admin.js 載 admin-topnav.js。
if (document.querySelector('[data-cr-form]')) {
  import('./scripts/components/cr-form-card.js')
}
import './scripts/scroll-reveal.js'
import './scripts/carousel.js'

// import '@fontsource/inter';
// import '@fontsource/noto-sans-tc';
// import '@fontsource/outfit';
// Future: replace Google Fonts CDN with @fontsource self-host for PDPA compliance
// 切換步驟：1) 移除 index.html 的 Google Fonts <link> 2) 取消註解上方 3 行
